/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { TypescriptComponent } from 'components/typescriptExample'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'

import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { Image, Link, ScrollTracking } from '@ta-interaktiv/contentbricks'

// TODO Set the correct article ID here
const ARTICLE_ID = '19982456'

export function AppComponent() {
  const isMobile = window.innerWidth < 600
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      {/* TODO Replace the following segment with your project cover */}
      <header className='ui vertical fitted segment'>
        <div className='ui container'>
          <h1 className='ui header' style={{ marginBottom: 0 }}>
            Les communes où les voitures polluent le plus
          </h1>
          <h2 className='ui header' style={{ marginTop: 0 }}>
            <div className='small sub header'>
              Une analyse des données montre pour la première fois où
              se trouvent les voitures à fortes émissions de CO₂. Quelle
              est la situation dans votre commune?  
            </div>
          </h2>
          <p className='ta author'>
            <strong>Dominik Balmer, Fanny Giroud, Mathias Born</strong>
          </p>
        </div>
      </header>

      {/* TODO And probably remove the following segment and replace it with
       your own content */}
      <div className='ui vertical segment'>
        <div className='ui text container' style={{ marginTop: '0rem' }}>
          <p>
            Les automobilistes contribuent de manière significative au mauvais équilibre climatique de la Suisse. Selon l'Office fédéral de l'environnement, on estime qu'environ un tiers de toutes les émissions de dioxyde de carbone en Suisse (CO₂) proviennent du trafic routier. Et les trois quarts de cette somme proviennent des voitures particulières. Le CO₂ est un gaz à effet de serre qui accélère le changement climatique.
          </p>
          <p>
            Les politiciens tentent de contrecarrer cette tendance sans grand succès, comme le montre un nouveau rapport de l'Office fédéral de l'énergie (OFEN). 
            Depuis 2012, les importateurs de voitures sont tenus de limiter les émissions des voitures de tourisme neuves à la valeur cible de 130 g de CO₂ en moyenne par kilomètre. Cette valeur limite est contraignante depuis 2015. Cependant, cet objectif n’a pas été atteint jusqu’à présent. Les importateurs qui ne sont pas en mesure de respecter cette valeur doivent payer des amendes - en 2018, celles-ci s'élevaient à environ 32 millions de francs.
          </p>
          <p>
            Il est clair que ces sanctions seront beaucoup plus élevées à l'avenir. Depuis le 1er janvier, une nouvelle valeur cible de 95 g de CO₂ par kilomètre s'applique. Et, contre toute attente, les émissions de CO₂ des nouvelles voitures ont même augmenté ces dernières années au lieu de diminuer. 
          </p>
          <p>
            Pour le bilan général, le facteur décisif est avant tout la quantité de CO₂ émise par les voitures existantes. Notre analyse de données se penche sur cette question et montre dans quelles communes se trouvent les automobiles les plus nuisibles à l'environnement. Elle se fonde sur des données provenant de l'Office fédéral des routes (OFROU) et porte sur environ 4,6 millions de voitures de tourisme. 
          </p>
        </div>
        <div className='ui text container' style={{ marginTop: '4rem' }}>
          <h3 className='ui infographic header small'>
            Les six grandes villes
          </h3>
          {/* <p className='sectionIntro'>
            Die Fahrzeuge in den sechs Grossstädten haben einen Anteil von:
          </p> */}
          {/* <div className='barBylineTop'>4.6 Mio.</div> */}
          <div className='barWrapper'>
            <div className='bar' style={{ width: 8.0 + '%' }} />
          </div>
          <div className='barByline'>
            <div className='barBylineLeft'>8% de toutes les voitures en Suisse</div>
            <div className='barBylineRight'>4.6 Mio.</div>
          </div>
          <p className='readerExample'>
            <span className='black'>Exemple:</span> L’émission moyenne des voitures immatriculées dans la ville de Bâle est de 163 g de CO₂ par kilomètre. Ce chiffre est juste supérieur à la moyenne suisse de 160 g/km.
          </p>

          <div className='legendWrapper'>
            <div className='legendItem'>
              <div className='squareOne' />
              <div className='linechartText'>Villes les moins polluantes</div>
            </div>
            <div className='legendItem'>
              <div className='squareTwo' />
              <div className='linechartText'>Villes les plus polluantes</div>
            </div>
          </div>
        </div>
        <div className='ui container'>
          <Image
            file={[
              require('../../images/mobile-grossstaedte.png'),
              require('../../images/desktop-grossstaedte.png')
            ]}
            source='Matériel cartographique : Office fédéral de la statistique (OFS), GEOSTAT'
          />
        </div>

        <div className='ui text container' style={{ marginTop: '2rem' }}>
          <div className='iframeContainer'>
            <AutofitIFrame
              title='Liste des émission de CO₂ des voitures dans les grandes villes'
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/7Ei88/'
              height={isMobile ? '440' : '340'}
              width='581'
            />
          </div>
          <p>
            Les citadins se considèrent généralement comme respectueux de l'environnement. Toutefois, cela ne s'applique pas à tous les automobilistes urbains, comme le montre notre analyse. Par exemple, l'émission moyenne des voitures dans la ville de Bâle est d'environ 163 g de CO₂ par kilomètre. Cela place la ville au-dessus de la moyenne nationale de 160 g, tout comme la ville de Genève.</p>
            <p>Les données montrent que le parc de voitures est plus ancien à Genève (2009 en moyenne) qu’à Lausanne, par exemple (2011 en moyenne), qui est la grande ville avec les émissions moyennes de CO₂ les plus basses: moins de 150 g de CO₂ par kilomètre. Les Lausannois commencent à être connus en Suisse pour leur attitude critique envers les tout-terrain, qui sont lourds et consomment beaucoup. Une initiative visant à interdire les véhicules tout-terrain et les véhicules utilitaires sport (SUV) en ville a été lancée à la fin de l’année dernière.
          </p>
          <p>
            Les émissions de CO₂ moyennes des voitures lausannoises restent malgré tout bien supérieures aux valeurs “exemplaires” enregistrées par certaines petites communes rurales du canton de Vaud ou du Tessin.
          </p>
        </div>
        <div className='ui text container' style={{ marginTop: '4rem' }}>
          <h3 className='ui infographic header small'>Les communes de montagne</h3>
          <div className='barWrapper'>
            <div className='bar' style={{ width: 26 + '%' }} />
          </div>
          <div className='barByline'>
            <div className='barBylineLeft'>26% de toutes les voitures en Suisse</div>
            <div className='barBylineRight'>4.6 Mio.</div>
          </div>
          <p className='readerExample'>
            <span className='black'>Exemple:</span> Les émissions moyennes de CO₂ pour toutes les voitures immatriculées à Riemenstalden, dans le canton de Schwytz, sont d'environ de 205 g/km. La moyenne de toutes les voitures en Suisse est d'environ 160 g/km.
          </p>

          <div className='legendWrapper'>
            <div className='legendItem'>
              <div className='squareOne' />
              <div className='linechartText'>Communes les moins polluantes</div>
            </div>
            <div className='legendItem'>
              <div className='squareTwo' />
              <div className='linechartText'>Communes les plus polluantes</div>
            </div>
          </div>
        </div>
        <div className='ui container'>
          <Image
            file={[
              require('../../images/mobile-berggemeinde.png'),
              require('../../images/desktop-berggemeinde.png')
            ]}
            source='Matériel cartographique : Office fédéral de la statistique (OFS), GEOSTAT'
          />
        </div>
        <div className='ui text container' style={{ marginTop: '2rem' }}>
          <div className='iframeContainer'>
            <AutofitIFrame
              title='Liste des émission de CO₂ des voitures dans les communes de montagne'
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/UyeuC/'
              height={isMobile ? '440' : '340'}
              width='581'
            />
          </div>
          <p>
            Riemenstalden détient un record peu glorieux. Dans cette commune montagneuse du canton de Schwyz, les habitants conduisent des voitures particulièrement nocives pour le climat. Selon notre analyse, il y avait environ 40 voitures immatriculées à Riemenstalden à la fin du mois de janvier 2020 - pour moins de 100 habitants. La plupart de ces voitures ont quatre roues motrices. Or les tout-terrain sont tout sauf respectueux du climat. L'émission moyenne de CO₂ par voiture dans cette commune de montagne est de 205 g/km. Selon les experts, cela correspond à une consommation moyenne de carburant de 8,9 l aux 100 kilomètres.
          </p>
          <p>
            Ces valeurs records ne semblent pas préoccuper le syndic, Martin Gisler. "Tant que les gens volent encore autour du monde, je n'ai aucun problème avec cela." À Riemenstalden, explique-t-il, les gens dépendent des voitures. "Nous n'avons presque pas de transports publics." Ce n'est que le matin et l'après-midi qu'un bus postal se rend dans la commune. M. Gisler lui-même possède une VW comme voiture familiale et une jeep pour le transport du bétail. "Nous ne faisons que quelques kilomètres par an avec nos voitures." Lui-même ne part jamais en vacances. Il faut en tenir compte. En outre, les voitures électriques ne sont pas aussi écologiques qu'on le dit toujours - c'est particulièrement vrai pour la production et l'élimination des batteries. 
          </p>
          <p>
            Les experts automobiles de l’Association Transports et Environnement (ATE) ont un point de vue différent. Ils fondent de grands espoirs sur les voitures électriques en particulier. "À l’avenir, de plus en plus de modèles électriques suffisamment puissants pour les régions montagneuses suisses seront disponibles", déclare Anette Michel, cheffe de projet pour <a href='http://www.ecomobiliste.ch/fr.html'>l'”Ecomobiliste”</a>, le guide de l’ATE. Les voitures électriques sont susceptibles de devenir encore plus attrayantes, notamment dans les régions montagneuses, "car l'énergie de freinage peut être récupérée lors de la conduite en descente".
          </p>
          <p>
            Notre analyse montre également qu'il y a des communes de montagne qui ont relativement peu de voitures polluantes - par exemple au Tessin, un canton où le taux de motorisation est pourtant très élevé.
          </p>
        </div>

        <div className='ui text container' style={{ marginTop: '4rem' }}>
          <h3 className='ui infographic header small'>
            Les communes urbaines
          </h3>
          <div className='barWrapper'>
            <div className='bar' style={{ width: 41.6 + '%' }} />
          </div>
          <div className='barByline'>
            <div className='barBylineLeft'>41.6% de toutes les voitures en Suisse</div>
            <div className='barBylineRight'>4.6 Mio.</div>
          </div>
          <p className='readerExample'>
            <span className='black'>Exemple:</span> Les émissions moyennes de CO₂ de toutes les voitures immatriculées dans la municipalité urbaine de Wollerau sont d'environ 173 g de CO₂ par kilomètre.
          </p>
          <div className='legendWrapper'>
            <div className='legendItem'>
              <div className='squareOne' />
              <div className='linechartText'>Communes les moins polluantes</div>
            </div>
            <div className='legendItem'>
              <div className='squareTwo' />
              <div className='linechartText'>Communes les plus polluantes</div>
            </div>
          </div>
        </div>
        <div className='ui container'>
          <Image
            file={[
              require('../../images/mobile-staedte.png'),
              require('../../images/desktop-staedte.png')
            ]}
            source='Matériel cartographique : Office fédéral de la statistique (OFS), GEOSTAT'
          />
        </div>
        <div className='ui text container' style={{ marginTop: '2rem' }}>
          <div className='iframeContainer'>
            <AutofitIFrame
              title='Liste des émission de CO₂ des voitures dans les villes'
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/Tvkzh/'
              height={isMobile ? '440' : '340'}
              width='581'
            />
          </div>
          <p>
          De nombreux clichés se confirment dans la catégorie des petites villes et des communes urbaines. Notre analyse montre que les communes riches, ont clairement des émissions plus élevées. Les communes de la Goldküste zurichoise se placent dans le peloton de tête, mais c'est Wollerau, paradis fiscal du canton de Schwyz, qui se hisse en tête de ce classement peu glorieux. Étonnamment, Wollerau a officiellement reçu le label Cité de l'énergie de l'Office fédéral de l'énergie et l’indique en bonne place sur son site web. Ce label est attribué par l'Office fédéral de l'énergie aux municipalités "qui ont mis en place une gestion de qualité pour la mise en œuvre de leur politique énergétique et environnementale". Selon le site web, Wollerau, par exemple, revendique une "stratégie énergétique 2020 soigneusement élaborée". Au vu des émissions de dioxydes de carbone records de cette commune, la dimension automobile ne semble pas en faire partie. 
          </p>
          <p>
          Le secrétaire municipal de Wollerau, Andreas Meyerhans, prend note du résultat: “Nous remarquons que les habitants de notre municipalité achètent des voitures qui ont un des émissions légèrement supérieures en CO₂. Cependant, nous ne pouvons pas, en tant que commune, influencer cette situation.” 
          </p>
          <p>
          Le niveau de vie influence les émissions de dioxyde de carbone moyennes des communes. C’est "l’un des critères décisifs" dans le choix d’une voiture, estime Anette Michel. C'est ce que montrent les statistiques sur les nouvelles voitures. C'est à Zoug que les émissions de CO₂ sont les plus élevées; les valeurs d'Obwald et du Tessin sont les plus faibles. "La Suisse est un pays riche, rappelle Micaël Tille, professeur de transport et d'infrastructure à l'EPFL. Les gens d'ici achètent généralement des voitures plus puissantes et plus lourdes que dans d'autres pays. Si vous devez payer une amende environnementale à cause de cela, personne ne s'en préoccupe."
          </p>
          <p>
          À noter que la commune avec le taux le plus bas de CO₂ est Montagny-près-Yverdon (VD), car plus de 500 véhicules des centres médicaux-sociaux (CMS) vaudois y sont immatriculés. Les émissions carbones sont basses car ces voitures, qui font l’objet d’un appel d’offres, sont choisies notamment selon des critères écologiques. “Nous remplaçons nos véhicules tous les quatre ans au plus tard et nous sommes très regardants concernant les émissions de CO₂”, explique le responsable des véhicules, Alain Barraud. Cela permet d’obtenir le rabais de la taxe auto.” 
          </p>
        </div>
        <div className='ui text container' style={{ marginTop: '4rem' }}>
          <h3 className='ui infographic header small'>Les communes rurales</h3>
          <div className='barWrapper'>
            <div className='bar' style={{ width: 9.9 + '%' }} />
          </div>
          <div className='barByline'>
            <div className='barBylineLeft'>9.9% de toutes les voitures en Suisse</div>
            <div className='barBylineRight'>4.6 Mio.</div>
          </div>
          <p className='readerExample'>
            <span className='black'>Exemple:</span> Les émissions moyennes de CO₂ de toutes les voitures immatriculées dans la communauté rurale de Kilchberg (BL) sont d'environ 183 g de CO₂ par kilomètre.
          </p>
          <div className='legendWrapper'>
            <div className='legendItem'>
              <div className='squareOne' />
              <div className='linechartText'>Communes les moins polluantes</div>
            </div>
            <div className='legendItem'>
              <div className='squareTwo' />
              <div className='linechartText'>Communes les plus polluantes</div>
            </div>
          </div>
        </div>
        <div className='ui container'>
          <Image
            file={[
              require('../../images/mobile-land.png'),
              require('../../images/desktop-land.png')
            ]}
            source='Matériel cartographique : Office fédéral de la statistique (OFS), GEOSTAT'
          />
        </div>
        <div className='ui text container' style={{ marginTop: '2rem' }}>
          <div className='iframeContainer'>
            <AutofitIFrame
              title='Liste des émission de CO₂ des voitures dans les communes rurales'
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/vBEVd/'
              height={isMobile ? '440' : '340'}
              width='581'
            />
          </div>
          <p>
          Les 17 communes rurales avec les émissions de CO₂ moyennes les plus basses sont vaudoises. Là encore, le Service des autos n’a aucune explication sur ce phénomène. Le village de Boulens détient le record des émissions de CO₂ les plus basses (138,4 g/km). La proportion de véhicules récents y est assez haute: 30% des voitures datent d’après 2016, contre 20% en moyenne en Suisse.
          </p>
          <p>
          Le syndic du village de 361 habitants, Claude Wenger, a d’autres pistes. "Nous avions un garagiste Fiat qui vendait des petits gabarits, se rappelle-t-il. La commune a aussi mis en place depuis une décennie un système de covoiturage sur son site internet."
          </p>
          <p>
          Quant aux communes les plus polluantes, le trio de tête se trouve dans le canton de Bâle-Campagne: Kilchberg, Häfelfingen et Liesberg. Le Service des autos dit n’avoir "aucune explication".
          </p>
          <p>
          Les contrastes entre cantons expliquent sans doute en partie ces résultats. Zoug et Zurich ont les valeurs les plus hautes, tandis que Vaud et le Tessin ont les émissions de CO₂ moyennes les plus basses.  
          </p>
          <p>
          Même les experts ne savent pas exactement pourquoi ces cantons ont des valeurs faibles. Une possibilité est l'octroi de primes par les différents cantons pour l'achat de voitures électriques - le canton du Tessin, par exemple, dispose d'un tel modèle. 
          </p>
        </div>
        <div className='ui text container' style={{ marginTop: '4rem' }}>
          <h3 className='ui infographic header small'>
            Les communes entre ville et campagne
          </h3>
          <div className='barWrapper'>
            <div className='bar' style={{ width: 14.6 + '%' }} />
          </div>
          <div className='barByline'>
            <div className='barBylineLeft'>14.6% de toutes les voitures en Suisse</div>
            <div className='barBylineRight'>4.6 Mio.</div>
          </div>
          <p className='readerExample'>
            <span className='black'>Exemple:</span> L'émission moyenne de CO₂ de toutes les voitures immatriculées dans la municipalité de Dittingen (BL) est d'environ 174 g de CO₂ par kilomètre.
          </p>

          {/* <h4 className='titleGraphic'>
            Die Gemeinden zwischen Stadt und Land
          </h4> */}
          <div className='legendWrapper'>
            <div className='legendItem'>
              <div className='squareOne' />
              <div className='linechartText'>Communes les moins polluantes</div>
            </div>
            <div className='legendItem'>
              <div className='squareTwo' />
              <div className='linechartText'>Communes les plus polluantes</div>
            </div>
          </div>
        </div>
        <div className='ui container'>
          <Image
            file={[
              require('../../images/mobile-intermediaer.png'),
              require('../../images/desktop-intermediaer.png')
            ]}
            source='Matériel cartographique : Office fédéral de la statistique (OFS), GEOSTAT'
          />
        </div>
        <div className='ui text container' style={{ marginTop: '2rem' }}>
          <div className='iframeContainer'>
            <AutofitIFrame
              title='Liste des émission de CO₂ des voitures dans les communes intermédiaires'
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/2H29y/'
              height={isMobile ? '440' : '340'}
              width='581'
            />
          </div>
          <p>
          Près de 400 communes sont classées comme "intermédiaires" entre villes et campagne,par exemple Échallens (VD) ou Bardonnex (GE). Les émissions carbones de ce type de commune sont proches des municipalités rurales, certaines communes du canton de Bâle-Campagne en tête. Les petites villes de Suisse romande se distinguent par des valeurs faibles, comme Éclépens (VD) avec 143,5 g de CO₂/km ou Cossonay (VD) avec 143,.7 g CO₂/km.
          </p>
          <p>
          Dielsdorf est un cas particulier. La municipalité proche de l'aéroport de Zurich a des valeurs de CO₂ curieusement basses: sur l'ensemble des voitures, la moyenne est de 145 g/km. La proportion de voitures électriques est l’une des plus hautes du pays, derrière Zermatt (VS) et Saas-Fee (VS), ce qui influence beaucoup les valeurs de CO₂. 
          </p>
          <p>
          Selon notre analyse, une voiture sur trois est une BMW. Sandro Kälin, porte-parole de BMW Suisse, confirme que "tous les véhicules de la flotte du groupe BMW Suisse sont enregistrés auprès de la société basée à Dielsdorf ZH". 
          </p>
        </div>
      </div>

      <div
        className='ui container center aligned'
        style={{ margin: isMobile ? '1.2em 0 3em' : '0 0 3em' }}
      >
        <h4 style={{fontFamily: "TamediaChart,Helvetica,Arial,sans-serif", fontWeight: 700}} className='teaser'>Plus d'articles sur ce thème</h4>
        <ArticleTeasers numberOfCards='three' articleIds={['26658009', '18405807', '13966405']} />
      </div>
        

      {/* TODO Add your credits in this component */}
      <Footer
        locale='fr'
        hidePortfolioTeaser
        mailTo='fanny.giroud@24heures.ch'
        articleId={ARTICLE_ID}
        credits={[
          [
            'Concept, recherche et texte',
            'Dominik Balmer, Mathias Born et Fanny Giroud'
          ],
          ['Conception', 'Patrick Vögeli']
        ]}
      >
        <div className='ui vertical grey segment' id='methodik'>
          <div
            className='ui text container methodik'
            style={{ position: 'relative' }}
          >
            <div className='innerWrapperMethodik'>
              <h4>Méthodologie</h4>
              <p>
                Nous avons analysé les données de l'Office fédéral des routes (OFROU) recensant les 4,6 millions de voitures de tourisme immatriculées en Suisse. Toutes les voitures dont le propriétaire a donné une adresse en Suisse sont prises en compte. La base de données a été extraite en janvier 2020. Cependant, comme toutes les communes ne sont pas à jour, l'évaluation a été basée sur le statut des communes de 2018. Selon l’OFROU, environ 400 voitures, dont les valeurs ont été enregistrées selon une nouvelle méthode, ont été mises en circulation en 2020. Leur influence sur la valeur moyenne est négligeable. Dans plusieurs milliers de cas, aucune valeur de CO₂ n’est enregistrée, notamment les véhicules anciens et importés. Ceux-ci n'ont pas été pris en compte dans nos calculs. Nous avons adopté la classification des communes de l'Office fédéral de la statistique.
              </p>
              <p>Le code utilisé pour l’analyse des données: <a href="https://github.com/tamedia-ddj/2020-03-07_CO₂">https://github.com/tamedia-ddj/2020-03-07_CO₂</a></p>
            </div>
          </div>
        </div>
      </Footer>
    </div>
  )
}
